import { History } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useCallback, useRef, useState, type ReactNode } from "react";
import SearchField from "../SearchField.tsx";
import AddFilterMenu from "./AddFilterMenu.tsx";
import FilterPill from "./FilterPill.tsx";
import type { Filters } from "./useFilters.ts";
import { useSlashFocus } from "./useSlashFocus.ts";

interface FilterBarProps {
  filters: Filters;
  /** Right side: what the filters leave ("1,204 companies", "12 of 340"). */
  count?: ReactNode;
  /** Right side, before the count: a `SavedViewsMenu`. */
  views?: ReactNode;
  /** Right side, last: a column picker, a refresh button. */
  actions?: ReactNode;
  /** Left of the search: a `ScopeToggle`. */
  leading?: ReactNode;
  /**
   * `toolbar` (default) is the strip at the top of a `TableCard`; `inline` sits
   * in a Panel's action or under a page header, without the strip.
   */
  variant?: "toolbar" | "inline";
  searchWidth?: number;
}

/**
 * A list's search and filters: the search field, one pill per filter shown,
 * "+ Filter" for the rest, "Clear all" back to the defaults, saved views and
 * the result count. "/" focuses the search.
 */
export default function FilterBar({
  filters,
  count,
  views,
  actions,
  leading,
  variant = "toolbar",
  searchWidth = 280,
}: FilterBarProps) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const handled = useCallback(() => setOpenKey(null), []);
  useSlashFocus(searchRef, !!filters.search);

  const right = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", flexShrink: 0 }}>
      {filters.activeCount > 0 && (
        <Button size="small" color="neutral" onClick={filters.clearAll}>
          Clear all
        </Button>
      )}
      {views}
      {count != null && (
        <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
          {count}
        </Typography>
      )}
      {actions}
    </Box>
  );

  // Pills wrap on the left; the right cluster stays on the first line.
  const row = (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, flex: 1, minWidth: 0 }}>
        {leading}
        {filters.search && (
          <SearchField
            value={filters.search.value}
            onChange={filters.search.set}
            placeholder={filters.search.placeholder ?? "Search"}
            minWidth={searchWidth}
            inputRef={searchRef}
            shortcut="/"
            clearable
          />
        )}
        {filters.visible.map((spec) => (
          <FilterPill
            key={spec.key}
            spec={spec}
            filters={filters}
            openRequested={openKey === spec.key}
            onOpenHandled={handled}
          />
        ))}
        <AddFilterMenu
          specs={filters.hidden}
          onPick={(key) => {
            filters.add(key);
            setOpenKey(key);
          }}
        />
      </Box>
      <Box sx={{ minHeight: 34, display: "flex", alignItems: "center" }}>{right}</Box>
    </Box>
  );

  const hint = filters.restored && (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1, color: "text.secondary" }}>
      <History sx={{ fontSize: 14 }} />
      <Typography variant="caption" sx={{ color: "inherit" }}>
        Showing your last filters ·{" "}
        <Link component="button" variant="caption" onClick={filters.clearAll} underline="hover">
          Clear
        </Link>{" "}
        ·{" "}
        <Link component="button" variant="caption" onClick={filters.dismissRestored} underline="hover" color="inherit">
          Keep
        </Link>
      </Typography>
    </Box>
  );

  if (variant === "inline") {
    return (
      <Box>
        {row}
        {hint}
      </Box>
    );
  }
  return (
    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
      {row}
      {hint}
    </Box>
  );
}
